import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import {
  Building2, CreditCard, Landmark, Banknote, Save, Loader2,
  CheckCircle2, EyeOff, Trash2, Info, Mail, Send, Wifi, RotateCcw, ChevronDown, ChevronUp,
  Upload, ImageIcon,
} from "lucide-react";

export default function AdminSettings() {
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("company");

  const load = async () => {
    try {
      const r = await api.get("/admin/settings");
      setData(r.data);
      setForm({ ...r.data, stripe_secret_key: "" });
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (e) => {
    e?.preventDefault();
    setSaving(true);
    try {
      // Only send fields with meaningful values (don't blank the masked secret)
      const payload = { ...form };
      if (payload.stripe_secret_key === "" || payload.stripe_secret_key?.includes("…")) {
        delete payload.stripe_secret_key;
      }
      delete payload.stripe_secret_key_set;
      await api.patch("/admin/settings", payload);
      toast.success("Paramètres enregistrés");
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const clearStripeKey = async () => {
    if (!window.confirm("Supprimer la clé secrète Stripe enregistrée ? Le pod reviendra à la clé par défaut.")) return;
    try {
      await api.delete("/admin/settings/stripe-key");
      toast.success("Clé Stripe supprimée");
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  if (!form) {
    return <div className="p-12 text-center text-fegray"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>;
  }

  const tabs = [
    { key: "company", label: "Mes coordonnées", icon: Building2 },
    { key: "stripe", label: "Carte bancaire", icon: CreditCard, status: form.stripe_enabled },
    { key: "bank", label: "Virement", icon: Landmark, status: form.bank_transfer_enabled },
    { key: "on_site", label: "Sur place", icon: Banknote, status: form.pay_on_site_enabled },
    { key: "email", label: "Emails", icon: Mail, status: form.smtp_enabled },
  ];

  return (
    <div className="p-8 lg:p-10 max-w-5xl">
      <p className="text-fegreen font-medium uppercase tracking-[0.25em] text-xs mb-2">— Configuration</p>
      <h1 className="text-3xl lg:text-4xl font-bold text-fegray-dark mb-2">Paramètres du pod</h1>
      <p className="text-sm text-fegray mb-8">
        Renseignez vos coordonnées d&apos;entreprise et activez les moyens de paiement proposés à vos clients.
      </p>

      <div className="flex flex-wrap gap-2 mb-7" data-testid="settings-tabs">
        {tabs.map((t) => (
          <button
            key={t.key}
            onClick={() => setActiveTab(t.key)}
            data-testid={`settings-tab-${t.key}`}
            className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-bold transition-all ${
              activeTab === t.key
                ? "bg-fegreen text-white shadow"
                : "bg-white border border-gray-200 text-fegray-dark hover:border-fegreen"
            }`}
          >
            <t.icon className="w-4 h-4" /> {t.label}
            {t.status !== undefined && (
              <span className={`w-2 h-2 rounded-full ${t.status ? "bg-fegreen-light ring-2 ring-white" : (activeTab === t.key ? "bg-white/40" : "bg-gray-300")}`} />
            )}
          </button>
        ))}
      </div>

      <form onSubmit={save} className="fe-card p-7 lg:p-8 space-y-6">
        {activeTab === "company" && <CompanySection form={form} set={set} />}
        {activeTab === "stripe" && (
          <StripeSection form={form} set={set} setKeySet={data?.stripe_secret_key_set} onClear={clearStripeKey} />
        )}
        {activeTab === "bank" && <BankSection form={form} set={set} />}
        {activeTab === "on_site" && <OnSiteSection form={form} set={set} />}
        {activeTab === "email" && <EmailSection form={form} set={set} setPasswordSet={data?.smtp_password_set} reload={load} />}

        <div className="pt-5 border-t border-gray-100 flex justify-end">
          <button
            type="submit"
            disabled={saving}
            data-testid="settings-save-btn"
            className="btn-green disabled:opacity-50 !py-2.5 !px-7 text-sm"
          >
            {saving
              ? <><Loader2 className="w-4 h-4 animate-spin" /> Enregistrement…</>
              : <><Save className="w-4 h-4" /> Enregistrer les paramètres</>
            }
          </button>
        </div>
      </form>
    </div>
  );
}

function CompanySection({ form, set }) {
  return (
    <Section title="Mes coordonnées" subtitle="Ces informations seront utilisées sur vos factures et reçus URSSAF.">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Nom commercial" value={form.company_name} onChange={(v) => set("company_name", v)} testid="company_name" placeholder="FORM'ELEC" />
        <Field label="Forme juridique" value={form.company_legal_form} onChange={(v) => set("company_legal_form", v)} testid="company_legal_form" placeholder="Auto-entrepreneur, EURL…" />
        <Field label="SIRET" value={form.siret} onChange={(v) => set("siret", v)} testid="siret" placeholder="14 chiffres" />
        <Field label="Code APE / NAF" value={form.ape_code} onChange={(v) => set("ape_code", v)} testid="ape_code" placeholder="9313Z" />
        <Field label="Agrément SAP (Services à la Personne)" value={form.sap_agrement} onChange={(v) => set("sap_agrement", v)} testid="sap_agrement" placeholder="SAP123456789" />
        <Field label="N° TVA intracommunautaire (si applicable)" value={form.vat_number} onChange={(v) => set("vat_number", v)} testid="vat_number" placeholder="FR..." />
        <Field label="Email professionnel" type="email" value={form.company_email} onChange={(v) => set("company_email", v)} testid="company_email" placeholder="rosalie@form-elec.fr" />
        <Field label="Téléphone" value={form.company_phone} onChange={(v) => set("company_phone", v)} testid="company_phone" placeholder="07 66 25 31 43" />
        <div className="md:col-span-2">
          <Field label="Adresse" value={form.company_address} onChange={(v) => set("company_address", v)} testid="company_address" />
        </div>
        <Field label="Code postal" value={form.company_postal_code} onChange={(v) => set("company_postal_code", v)} testid="company_postal_code" />
        <Field label="Ville" value={form.company_city} onChange={(v) => set("company_city", v)} testid="company_city" placeholder="Istres" />
      </div>
    </Section>
  );
}

function StripeSection({ form, set, setKeySet, onClear }) {
  return (
    <Section title="Stripe" subtitle="Acceptez les paiements par carte bancaire et l'avance immédiate URSSAF.">
      <Toggle
        label="Activer les paiements Stripe"
        sub="Le paiement par carte sera proposé à vos clients à la réservation."
        checked={form.stripe_enabled}
        onChange={(v) => set("stripe_enabled", v)}
        testid="stripe_enabled"
      />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-5">
        <Field label="Email du compte Stripe" type="email" value={form.stripe_account_email} onChange={(v) => set("stripe_account_email", v)} testid="stripe_account_email" placeholder="rosalie@form-elec.fr" />
        <Field label="Clé publique (publishable key)" value={form.stripe_publishable_key} onChange={(v) => set("stripe_publishable_key", v)} testid="stripe_publishable_key" placeholder="pk_live_..." />
      </div>
      <div className="mt-4">
        <label className="block text-xs uppercase tracking-wider text-fegray mb-2 font-bold">
          Clé secrète (secret key)
          {setKeySet && (
            <span className="ml-2 inline-flex items-center gap-1 text-[10px] text-fegreen font-extrabold">
              <CheckCircle2 className="w-3 h-3" /> Enregistrée
            </span>
          )}
        </label>
        <div className="flex gap-2">
          <input
            type="password"
            value={form.stripe_secret_key || ""}
            onChange={(e) => set("stripe_secret_key", e.target.value)}
            placeholder={setKeySet ? "Laissez vide pour conserver la clé enregistrée" : "sk_live_…"}
            data-testid="stripe_secret_key"
            className="flex-1 bg-white border border-gray-200 rounded-xl px-4 py-3 text-sm font-mono text-fegray-dark focus:border-fegreen focus:ring-2 focus:ring-fegreen/20 focus:outline-none"
          />
          {setKeySet && (
            <button
              type="button"
              onClick={onClear}
              data-testid="stripe-clear-key"
              className="inline-flex items-center gap-1.5 px-3 py-2.5 rounded-xl border border-red-200 text-red-500 text-xs font-bold hover:bg-red-50"
            >
              <Trash2 className="w-3.5 h-3.5" /> Supprimer
            </button>
          )}
        </div>
        <p className="mt-2 text-[11px] text-fegray flex items-start gap-1.5 leading-relaxed">
          <EyeOff className="w-3 h-3 mt-0.5 flex-shrink-0" />
          La clé est chiffrée à l&apos;affichage. Vous ne verrez que les 4 derniers caractères. Obtenez votre clé sur{" "}
          <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noopener noreferrer" className="text-fegreen underline">dashboard.stripe.com/apikeys</a>.
        </p>
      </div>
      <InfoBanner>
        Sans clé renseignée, le pod utilise une clé de test partagée par défaut. Pour encaisser réellement, configurez votre clé secrète <strong>live</strong> Stripe.
      </InfoBanner>
    </Section>
  );
}

function BankSection({ form, set }) {
  return (
    <Section title="Virement bancaire" subtitle="Affichez vos coordonnées bancaires pour les paiements par virement.">
      <Toggle
        label="Proposer le paiement par virement"
        sub="Les coordonnées seront affichées au client à l'étape de paiement."
        checked={form.bank_transfer_enabled}
        onChange={(v) => set("bank_transfer_enabled", v)}
        testid="bank_transfer_enabled"
      />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-5">
        <div className="md:col-span-2">
          <Field label="Titulaire du compte" value={form.bank_holder} onChange={(v) => set("bank_holder", v)} testid="bank_holder" placeholder="Rosalie RIVETI" />
        </div>
        <div className="md:col-span-2">
          <Field label="IBAN" value={form.bank_iban} onChange={(v) => set("bank_iban", v)} testid="bank_iban" placeholder="FR76 1234 5678 9012 3456 7890 123" mono />
        </div>
        <Field label="BIC / SWIFT" value={form.bank_bic} onChange={(v) => set("bank_bic", v)} testid="bank_bic" placeholder="BNPAFRPPXXX" mono />
        <Field label="Titulaire de l'IBAN (si différent)" value={form.iban_holder} onChange={(v) => set("iban_holder", v)} testid="iban_holder" placeholder="" />
      </div>
    </Section>
  );
}

function OnSiteSection({ form, set }) {
  return (
    <Section title="Paiement sur place" subtitle="Le client règle Rosalie en espèces ou par chèque lors de la première séance.">
      <Toggle
        label="Proposer le paiement sur place"
        sub="Le client peut confirmer sa réservation sans payer en ligne et règle sur place."
        checked={form.pay_on_site_enabled}
        onChange={(v) => set("pay_on_site_enabled", v)}
        testid="pay_on_site_enabled"
      />
      <div className="mt-5">
        <label className="block text-xs uppercase tracking-wider text-fegray mb-2 font-bold">
          Instructions affichées au client (optionnel)
        </label>
        <textarea
          rows={3}
          value={form.pay_on_site_instructions || ""}
          onChange={(e) => set("pay_on_site_instructions", e.target.value)}
          data-testid="settings-pay_on_site_instructions"
          placeholder="Paiement en espèces ou par chèque à remettre à Rosalie lors de la première séance."
          className="w-full bg-white border border-gray-200 rounded-xl px-4 py-2.5 text-sm text-fegray-dark focus:border-fegreen focus:ring-2 focus:ring-fegreen/20 focus:outline-none"
        />
      </div>
      <InfoBanner>
        Aucun encaissement automatique : le créneau est bloqué dès la confirmation et le règlement se fait en direct lors de la séance.
      </InfoBanner>
    </Section>
  );
}

function EmailSection({ form, set, setPasswordSet, reload }) {
  const [testing, setTesting] = useState(false);
  const [testingConn, setTestingConn] = useState(false);
  const [testTo, setTestTo] = useState("");

  const saveCurrent = async () => {
    const payload = { ...form };
    if (payload.stripe_secret_key === "" || payload.stripe_secret_key?.includes("…")) delete payload.stripe_secret_key;
    if (payload.smtp_password === "" || payload.smtp_password?.includes("…")) delete payload.smtp_password;
    delete payload.stripe_secret_key_set;
    delete payload.smtp_password_set;
    await api.patch("/admin/settings", payload);
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      await saveCurrent();
      const { data } = await api.post("/admin/settings/smtp/test", { to: testTo || undefined });
      toast.success(`Email de test envoyé à ${data.to}`);
      await reload();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setTesting(false);
    }
  };

  const testConnection = async () => {
    setTestingConn(true);
    try {
      await saveCurrent();
      const { data } = await api.post("/admin/settings/smtp/test-connection");
      toast.success(data.detail || "Connexion SMTP réussie");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setTestingConn(false);
    }
  };

  const clearPwd = async () => {
    if (!window.confirm("Supprimer le mot de passe SMTP enregistré ?")) return;
    try {
      await api.delete("/admin/settings/smtp-password");
      toast.success("Mot de passe SMTP supprimé");
      await reload();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <Section title="Notifications par email (SMTP)" subtitle="Configurez votre serveur SMTP pour envoyer automatiquement les confirmations de réservation et vous prévenir des nouvelles demandes.">
      <Toggle
        label="Activer l'envoi d'emails"
        sub="Désactivez si vous préférez ne pas envoyer d'emails automatiques."
        checked={form.smtp_enabled}
        onChange={(v) => set("smtp_enabled", v)}
        testid="smtp_enabled"
      />

      <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="md:col-span-2">
          <Field label="Serveur SMTP (host)" value={form.smtp_host} onChange={(v) => set("smtp_host", v)} testid="smtp_host" placeholder="ex. mail.form-elec.fr" mono />
        </div>
        <Field label="Port" value={form.smtp_port} onChange={(v) => set("smtp_port", v)} testid="smtp_port" placeholder="587" mono />
        <Field label="Identifiant / Email" value={form.smtp_user} onChange={(v) => set("smtp_user", v)} testid="smtp_user" placeholder="rosalie@form-elec.fr" />
        <div className="md:col-span-2">
          <label className="block text-xs uppercase tracking-wider text-fegray mb-2 font-bold">
            Mot de passe SMTP
            {setPasswordSet && (
              <span className="ml-2 inline-flex items-center gap-1 text-[10px] text-fegreen font-extrabold">
                <CheckCircle2 className="w-3 h-3" /> Enregistré
              </span>
            )}
          </label>
          <div className="flex gap-2">
            <input
              type="password"
              value={form.smtp_password || ""}
              onChange={(e) => set("smtp_password", e.target.value)}
              placeholder={setPasswordSet ? "Laissez vide pour conserver le mot de passe" : "••••••••"}
              data-testid="settings-smtp_password"
              className="flex-1 bg-white border border-gray-200 rounded-xl px-4 py-2.5 text-sm text-fegray-dark focus:border-fegreen focus:ring-2 focus:ring-fegreen/20 focus:outline-none"
            />
            {setPasswordSet && (
              <button type="button" onClick={clearPwd} data-testid="smtp-clear-pwd"
                className="inline-flex items-center gap-1.5 px-3 py-2.5 rounded-xl border border-red-200 text-red-500 text-xs font-bold hover:bg-red-50">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>
        <Field label="Email expéditeur" value={form.smtp_from_email} onChange={(v) => set("smtp_from_email", v)} testid="smtp_from_email" placeholder="rosalie@form-elec.fr" />
        <Field label="Nom expéditeur" value={form.smtp_from_name} onChange={(v) => set("smtp_from_name", v)} testid="smtp_from_name" placeholder="Rosalie — FORM'ELEC" />
        <Field label="Destinataire des notifications admin" value={form.smtp_admin_recipient} onChange={(v) => set("smtp_admin_recipient", v)} testid="smtp_admin_recipient" placeholder="rosalie@form-elec.fr" />
      </div>

      <div className="mt-5">
        <Toggle
          label="Connexion sécurisée (STARTTLS)"
          sub="Recommandé pour les ports 587 / 25 / 2525. Désactivez uniquement pour le port 465 (SSL implicite)."
          checked={form.smtp_use_tls}
          onChange={(v) => set("smtp_use_tls", v)}
          testid="smtp_use_tls"
        />
      </div>

      <div className="mt-5">
        <LogoUploader currentUrl={form.logo_url} reload={reload} />
      </div>

      <div className="mt-7 pt-5 border-t border-gray-100">
        <h3 className="text-sm font-bold text-fegray-dark mb-3">Préférences d&apos;envoi</h3>
        <div className="space-y-3">
          <Toggle label="Envoyer la confirmation au client" sub="Email automatique au client dès qu'une réservation est confirmée (CB / virement / sur place)." checked={form.send_client_confirmation_email} onChange={(v) => set("send_client_confirmation_email", v)} testid="send_client_confirmation_email" />
          <Toggle label="Me notifier d'une nouvelle réservation" sub="Vous recevez un email à l'adresse ci-dessus pour chaque nouvelle réservation." checked={form.notify_admin_on_new_reservation} onChange={(v) => set("notify_admin_on_new_reservation", v)} testid="notify_admin_on_new_reservation" />
        </div>
      </div>

      <div className="mt-6 pt-5 border-t border-gray-100">
        <h3 className="text-sm font-bold text-fegray-dark mb-1">Tester la configuration</h3>
        <p className="text-xs text-fegray mb-3">D&apos;abord la connexion (pas d&apos;email), puis un envoi de test complet.</p>
        <div className="flex flex-wrap gap-2 items-start">
          <button type="button" onClick={testConnection} disabled={testingConn}
            data-testid="smtp-test-connection-btn"
            className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border-2 border-fegreen text-fegreen text-xs font-bold hover:bg-fegreen-light/40 transition-colors disabled:opacity-50">
            {testingConn ? <><Loader2 className="w-4 h-4 animate-spin" /> Test…</> : <><Wifi className="w-3.5 h-3.5" /> Tester la connexion</>}
          </button>
          <div className="flex-1 min-w-[200px] flex gap-2">
            <input type="email" value={testTo} onChange={(e) => setTestTo(e.target.value)} data-testid="smtp-test-to"
              placeholder="Email pour recevoir le test"
              className="flex-1 bg-white border border-gray-200 rounded-xl px-4 py-2.5 text-sm text-fegray-dark focus:border-fegreen focus:ring-2 focus:ring-fegreen/20 focus:outline-none"
            />
            <button type="button" onClick={sendTest} disabled={testing} data-testid="smtp-test-btn"
              className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-fegreen text-white text-xs font-bold hover:bg-fegreen-dark transition-colors disabled:opacity-50 whitespace-nowrap">
              {testing ? <><Loader2 className="w-4 h-4 animate-spin" /> Envoi…</> : <><Send className="w-3.5 h-3.5" /> Envoyer un test</>}
            </button>
          </div>
        </div>
      </div>

      <EmailTemplatesPanel />

      <InfoBanner>
        <strong>PlanetHoster</strong> : utilisez <span className="font-mono">mail.votredomaine.fr</span> sur le port <span className="font-mono">587</span> avec STARTTLS, votre adresse email complète comme identifiant et le mot de passe du compte email cPanel/N0C.
      </InfoBanner>
    </Section>
  );
}

function LogoUploader({ currentUrl, reload }) {
  const [uploading, setUploading] = useState(false);
  const inputRef = React.useRef(null);

  const onPick = () => inputRef.current?.click();

  const onChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-uploading same file
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Logo trop volumineux (max 2 Mo)");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/admin/settings/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Logo téléchargé");
      await reload();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("Supprimer le logo personnalisé ? Le logo par défaut FORM'ELEC sera utilisé.")) return;
    try {
      await api.delete("/admin/settings/logo");
      toast.success("Logo supprimé");
      await reload();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div data-testid="logo-uploader">
      <label className="block text-xs uppercase tracking-wider text-fegray mb-2 font-bold">
        Logo dans la signature email
      </label>
      <div className="flex items-center gap-4 p-4 rounded-2xl bg-fegray-light border border-gray-100">
        <div className="w-20 h-20 bg-white rounded-xl border border-gray-200 flex items-center justify-center overflow-hidden shrink-0">
          {currentUrl ? (
            <img src={currentUrl} alt="" className="max-w-full max-h-full object-contain" />
          ) : (
            <ImageIcon className="w-7 h-7 text-fegray/40" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold text-fegray-dark truncate">
            {currentUrl ? "Logo personnalisé actif" : "Logo FORM'ELEC par défaut"}
          </p>
          <p className="text-[11px] text-fegray mt-0.5">PNG, JPG, WebP, SVG ou GIF — 2 Mo max.</p>
          <div className="flex flex-wrap gap-2 mt-3">
            <button
              type="button"
              onClick={onPick}
              disabled={uploading}
              data-testid="logo-upload-btn"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-fegreen text-white text-xs font-bold hover:bg-fegreen-dark disabled:opacity-50"
            >
              {uploading ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Envoi…</> : <><Upload className="w-3.5 h-3.5" /> {currentUrl ? "Remplacer" : "Téléverser"}</>}
            </button>
            {currentUrl && (
              <button
                type="button"
                onClick={remove}
                data-testid="logo-remove-btn"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-red-200 text-red-500 text-xs font-bold hover:bg-red-50"
              >
                <Trash2 className="w-3.5 h-3.5" /> Supprimer
              </button>
            )}
          </div>
        </div>
        <input
          ref={inputRef}
          type="file"
          accept=".png,.jpg,.jpeg,.webp,.svg,.gif,image/*"
          onChange={onChange}
          className="hidden"
          data-testid="logo-file-input"
        />
      </div>
    </div>
  );
}

function EmailTemplatesPanel() {
  const [tpl, setTpl] = useState(null);
  const [defaults, setDefaults] = useState(null);
  const [placeholders, setPlaceholders] = useState([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/email-templates")
      .then((r) => {
        setTpl(r.data.templates);
        setDefaults(r.data.defaults);
        setPlaceholders(r.data.placeholders);
      })
      .catch(() => {});
  }, []);

  const update = (key, field, value) => {
    setTpl((t) => ({ ...t, [key]: { ...t[key], [field]: value } }));
  };

  const resetOne = (key) => {
    if (!defaults?.[key]) return;
    if (!window.confirm("Réinitialiser ce modèle au texte par défaut ?")) return;
    setTpl((t) => ({ ...t, [key]: { ...defaults[key] } }));
  };

  const save = async () => {
    if (!tpl) return;
    setSaving(true);
    try {
      const { data } = await api.patch("/admin/email-templates", tpl);
      setTpl(data);
      toast.success("Modèles d'emails enregistrés");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const labels = {
    client_confirmation: "Confirmation de réservation (envoyée au client)",
    admin_notification: "Notification de nouvelle réservation (envoyée à vous)",
  };

  return (
    <div className="mt-7 pt-5 border-t border-gray-100" data-testid="email-templates-panel">
      <button type="button" onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between text-left mb-3 hover:text-fegreen transition-colors"
        data-testid="email-templates-toggle">
        <div>
          <h3 className="text-sm font-bold text-fegray-dark">Modèles d&apos;emails</h3>
          <p className="text-xs text-fegray">Personnalisez le sujet et le contenu des emails automatiques.</p>
        </div>
        {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>

      {open && tpl && (
        <div className="space-y-5">
          <div className="bg-fegreen-light/30 border border-fegreen/20 rounded-xl p-3 text-[11px] text-fegray-dark leading-relaxed">
            <strong>Variables disponibles :</strong>{" "}
            {placeholders.map((p) => <span key={p} className="font-mono text-fegreen mr-1">{p}</span>)}
            <br /><span className="text-fegray">La signature (logo + coordonnées) est ajoutée automatiquement.</span>
          </div>

          {Object.entries(tpl).map(([key, val]) => (
            <div key={key} className="border border-gray-200 rounded-2xl p-5" data-testid={`template-${key}`}>
              <div className="flex flex-wrap justify-between items-baseline gap-2 mb-3">
                <h4 className="text-sm font-bold text-fegray-dark">{labels[key] || key}</h4>
                <button type="button" onClick={() => resetOne(key)}
                  data-testid={`template-reset-${key}`}
                  className="text-[11px] text-fegray hover:text-fegreen font-semibold inline-flex items-center gap-1">
                  <RotateCcw className="w-3 h-3" /> Réinitialiser
                </button>
              </div>
              <div className="mb-3">
                <label className="block text-[10px] uppercase tracking-wider text-fegray mb-1 font-bold">Sujet</label>
                <input type="text" value={val.subject}
                  data-testid={`template-subject-${key}`}
                  onChange={(e) => update(key, "subject", e.target.value)}
                  className="w-full bg-white border border-gray-200 rounded-xl px-4 py-2 text-sm text-fegray-dark focus:border-fegreen focus:ring-2 focus:ring-fegreen/20 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-wider text-fegray mb-1 font-bold">Corps du message</label>
                <textarea rows={10} value={val.body}
                  data-testid={`template-body-${key}`}
                  onChange={(e) => update(key, "body", e.target.value)}
                  className="w-full bg-white border border-gray-200 rounded-xl px-4 py-2 text-xs font-mono text-fegray-dark focus:border-fegreen focus:ring-2 focus:ring-fegreen/20 focus:outline-none"
                />
              </div>
            </div>
          ))}

          <div className="flex justify-end">
            <button type="button" onClick={save} disabled={saving}
              data-testid="templates-save"
              className="btn-green disabled:opacity-50 !py-2 !px-5 text-xs">
              {saving ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Enregistrement…</> : <><Save className="w-3.5 h-3.5" /> Enregistrer les modèles</>}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function Section({ title, subtitle, children }) {
  return (
    <div>
      <h2 className="text-lg font-bold text-fegray-dark">{title}</h2>
      {subtitle && <p className="text-xs text-fegray mt-1 mb-5">{subtitle}</p>}
      {children}
    </div>
  );
}

function Field({ label, value, onChange, type = "text", placeholder, testid, mono }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-wider text-fegray mb-2 font-bold">{label}</label>
      <input
        type={type}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={`settings-${testid}`}
        className={`w-full bg-white border border-gray-200 rounded-xl px-4 py-2.5 text-sm text-fegray-dark focus:border-fegreen focus:ring-2 focus:ring-fegreen/20 focus:outline-none ${mono ? "font-mono" : ""}`}
      />
    </div>
  );
}

function Toggle({ label, sub, checked, onChange, testid }) {
  return (
    <label className="flex items-start gap-4 cursor-pointer">
      <button
        type="button"
        role="switch"
        aria-checked={!!checked}
        onClick={() => onChange(!checked)}
        data-testid={`settings-toggle-${testid}`}
        className={`relative shrink-0 w-12 h-7 rounded-full transition-colors ${checked ? "bg-fegreen" : "bg-gray-200"}`}
      >
        <span className={`absolute top-1 w-5 h-5 bg-white rounded-full shadow transition-all ${checked ? "left-6" : "left-1"}`} />
      </button>
      <div className="flex-1">
        <div className="text-sm font-bold text-fegray-dark">{label}</div>
        {sub && <div className="text-xs text-fegray mt-0.5">{sub}</div>}
      </div>
    </label>
  );
}

function InfoBanner({ children }) {
  return (
    <div className="mt-5 flex items-start gap-2.5 p-4 rounded-xl bg-fegreen-light/40 border border-fegreen/20 text-xs text-fegray-dark leading-relaxed">
      <Info className="w-4 h-4 text-fegreen mt-0.5 flex-shrink-0" />
      <div>{children}</div>
    </div>
  );
}
